import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';
import { faPauseCircle, faPlayCircle, faStopCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const ProfessionalPageMessageAudioRecorder = ({ onCancel, onSend }) => {
    const [isRecording, setIsRecording] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [recordTime, setRecordTime] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const recordTimeRef = useRef(0);

    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const recordPluginRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const ws = WaveSurfer.create({
            container: containerRef.current!,
            waveColor: '#009EFD',
            progressColor: '#2AF598',
            height: 40,
            barWidth: 4,
            barGap: 2,
            barRadius: 5,
            barHeight: 3,
            interact: true,
        });

        const record = ws.registerPlugin(RecordPlugin.create({
            renderRecordedAudio: true,
        }));

        wavesurferRef.current = ws;
        recordPluginRef.current = record;

        record.on('record-start', () => {
            setIsRecording(true);
            setCurrentTime(0);
            setDuration(0);
            setRecordTime(0);

            timerIntervalRef.current = setInterval(() => {
                recordTimeRef.current += 1;
                setRecordTime(recordTimeRef.current);
            }, 1000);
        });

        record.on('record-end', (blob: Blob) => {
            clearInterval(timerIntervalRef.current!);
            const url = URL.createObjectURL(blob);
            ws.load(url);
            setIsRecording(false);

            ws.once('ready', () => {
                setDuration(ws.getDuration());
                onSend(blob, formatTime(recordTimeRef.current));
            });
        });

        ws.on('timeupdate', (time: number) => {
            setCurrentTime(time);
        });

        ws.on('play', () => setIsPlaying(true));
        ws.on('pause', () => setIsPlaying(false));
        ws.on('finish', () => setIsPlaying(false));

        record.startRecording();

        return () => {
            ws.destroy();
            clearInterval(timerIntervalRef.current!);
        };
    }, []);

    const stopRecording = () => {
        recordPluginRef.current?.stopRecording();
    };

    const togglePlayback = () => {
        wavesurferRef.current?.playPause();
    };

    const cancelRecording = () => {
        wavesurferRef.current?.destroy();
        onCancel();
    };

    return (
        <div className="d-flex align-items-center justify-content-end gap-3 w-100">
            <FontAwesomeIcon
                icon={faTrash}
                style={{ fontSize: '1.5rem', cursor: 'pointer' }}
                onClick={cancelRecording}
            />
            <div className="responsive-audio-box p-2 gap-3 align-items-center d-grid"
                style={{ backgroundColor: '#e4e4e4', borderRadius: '10px', gridTemplateColumns: '1fr auto', width: '100%' }}>
                <div ref={containerRef} />
                <span style={{ fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {isRecording
                        ? formatTime(recordTime)
                        : `${formatTime(currentTime)} / ${formatTime(duration)}`}
                </span>
            </div>
            {isRecording ? (
                <FontAwesomeIcon
                    icon={faStopCircle}
                    style={{ fontSize: '1.8rem', cursor: 'pointer' }}
                    onClick={stopRecording}
                />
            ) : (
                <FontAwesomeIcon
                    icon={isPlaying ? faPauseCircle : faPlayCircle}
                    style={{ fontSize: '1.8rem', cursor: 'pointer' }}
                    onClick={togglePlayback}
                />
            )}
        </div>
    );
};

export default ProfessionalPageMessageAudioRecorder;